import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LayoutDashboard, Users, Inbox, Calendar, CalendarDays, DollarSign, Tag, FileText, Mail, MailOpen, Megaphone, Instagram, ShoppingBag, BookOpen, Library, Send, Bell, Mic, LogOut, Activity, ExternalLink, EyeOff, MessageSquareQuote, BellRing, Palette, Ribbon, Wallet, TrendingUp, Home, Rows3 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { BrandMark } from "@/components/BrandMark";

const links = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true, testid: "nav-overview" },
  { to: "/admin/home", icon: Home, label: "Home Page", testid: "nav-home-page" },
  { to: "/admin/pages", icon: FileText, label: "Pages", testid: "nav-pages" },
  { to: "/admin/llms", icon: FileText, label: "llms.txt (AI)", testid: "nav-llms" },
  { to: "/admin/clients", icon: Users, label: "Clients", testid: "nav-clients" },
  { to: "/admin/leads", icon: Inbox, label: "Leads", testid: "nav-leads" },
  { to: "/admin/bookings", icon: Calendar, label: "Bookings", testid: "nav-bookings" },
  { to: "/admin/calendar", icon: CalendarDays, label: "Calendar", testid: "nav-calendar" },
  { to: "/admin/lessons", icon: BookOpen, label: "Lessons", testid: "nav-lessons" },
  { to: "/admin/library", icon: Library, label: "Library", testid: "nav-library" },
  { to: "/admin/newsletter", icon: Send, label: "Newsletter", testid: "nav-newsletter" },
  { to: "/admin/popups", icon: BellRing, label: "Pop-Ups", testid: "nav-popups" },
  { to: "/admin/appearance", icon: Palette, label: "Appearance", testid: "nav-appearance" },
  { to: "/admin/email-templates", icon: MailOpen, label: "Email Templates", testid: "nav-email-templates" },
  { to: "/admin/reminders", icon: Bell, label: "Reminders", testid: "nav-reminders" },
  { to: "/admin/push", icon: BellRing, label: "Push Notifications", testid: "nav-push" },
  { to: "/admin/coaching", icon: Ribbon, label: "Coaching Inbox", testid: "nav-coaching" },
  { to: "/admin/ronin-box", icon: Mic, label: "Ronin Box", testid: "nav-ronin-box" },
  { to: "/admin/ronin-leads", icon: Users, label: "Ronin Leads", testid: "nav-ronin-leads" },
  { to: "/admin/meetups", icon: Megaphone, label: "Meetups", testid: "nav-meetups" },
  { to: "/admin/equipment", icon: ShoppingBag, label: "Equipment", testid: "nav-equipment" },
  { to: "/admin/payments", icon: DollarSign, label: "Payments", testid: "nav-payments" },
  { to: "/admin/pricing", icon: Wallet, label: "Pricing", testid: "nav-pricing" },
  { to: "/admin/marketing", icon: TrendingUp, label: "Marketing", testid: "nav-marketing" },
  { to: "/admin/offers", icon: Tag, label: "Offers", testid: "nav-offers" },
  { to: "/admin/blog", icon: FileText, label: "Blog", testid: "nav-blog" },
  { to: "/admin/stories", icon: MessageSquareQuote, label: "Stories", testid: "nav-stories" },
  { to: "/admin/page-visibility", icon: EyeOff, label: "Page Visibility", testid: "nav-page-visibility" },
  { to: "/admin/navigation", icon: Rows3, label: "Navigation Order", testid: "nav-navigation-order" },
  { to: "/admin/instagram", icon: Instagram, label: "Instagram", testid: "nav-instagram" },
  { to: "/admin/email", icon: Mail, label: "Email", testid: "nav-email" },
  { to: "/admin/health", icon: Activity, label: "Health Check", testid: "nav-health" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [openCount, setOpenCount] = useState(0);

  useEffect(() => {
    let alive = true;
    const fetchCount = () => api.get("/admin/coaching/summary")
      .then((r) => { if (alive) setOpenCount(r.data.open_count || 0); }).catch(() => {});
    fetchCount();
    const id = setInterval(fetchCount, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <div className="admin-theme-lock min-h-screen flex bg-[#050505]" data-testid="admin-layout">
      <aside className="w-64 border-r border-[#27272A] bg-[#0A0A0A] flex flex-col" data-testid="admin-sidebar">
        <div className="p-6 border-b border-[#27272A]">
          <BrandMark size="md" />
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-2">Trainer CRM</div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {links.map(({ to, icon: Icon, label, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-sm text-sm uppercase tracking-widest border ${
                  isActive
                    ? "border-[#FF331F] bg-[#FF331F]/10 text-white"
                    : "border-transparent text-zinc-400 hover:text-white hover:bg-[#121212]"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1">{label}</span>
              {to === "/admin/coaching" && openCount > 0 && (
                <span data-testid="coaching-badge" className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF331F] text-white text-[10px] font-bold flex items-center justify-center">
                  {openCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-[#27272A] space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center gap-3 px-3 py-3 rounded-sm text-sm uppercase tracking-widest border border-transparent text-zinc-300 hover:text-white hover:bg-[#121212]"
            data-testid="admin-view-site"
            title="Opens the public site in a new tab"
          >
            <ExternalLink className="h-4 w-4" /> View site
          </a>
          <div className="px-2 py-2 text-xs text-zinc-500 truncate">{user?.email}</div>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-zinc-400 hover:text-white text-sm uppercase tracking-widest"
            data-testid="admin-logout"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        <Outlet />
      </main>

      <Toaster position="top-right" theme="dark" />
    </div>
  );
}
